import { Box, Grid } from '@mui/material'
import React, { useMemo } from 'react'
import { isNil } from 'lodash-es'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import {
  StyledItemWrapper,
  StyledLabel,
  StyledValue,
  StyledValueWrapper,
} from '../../../../components/form/DisplayedItem'
import { useAdgroupContext } from '../../../../utils/hooks/adGroup/useAdgroupContext'
import { displayAsCurrency } from '../../../../utils/displayAsCurrency'
import { DailyBudgetType } from '../../../../__generated__/graphql'
import {
  getLeftoverBudgetValueColor,
  LeftoverBudgetKind,
} from '../../../../utils/hooks/adGroup/adgroupLeftoverBudgetDisplay'
import { FieldGroup } from './FieldGroup'
import { BudgetHistoryButtons } from './budgetHistory/BudgetHistoryButtons'

type SpentBudgetItem = {
  label: string
  spent?: number | null
  left?: number
  leftoverKind?: LeftoverBudgetKind
  /** Standalone amount (e.g. auto daily budget), not spent + left. */
  value?: number | null
}

const SpentBudget: React.FC<{ isWizard?: boolean }> = ({ isWizard = false }) => {
  if (isWizard) return null
  const [adGroup] = useAdgroupContext()
  const t = useAppTranslations()
  const { budget } = adGroup
  const measures = budget?.budgetMeasures

  const spentBudgetsAndLabels = useMemo(() => {
    const pushSpentWithLeft = (
      arr: SpentBudgetItem[],
      spentLabel: string,
      spent: number | null | undefined,
      cap: number | null | undefined,
      leftoverKind: LeftoverBudgetKind
    ) => {
      const item: SpentBudgetItem = { label: spentLabel, spent }
      if (!isNil(cap)) {
        item.left = (cap ?? 0) - (spent ?? 0)
        item.leftoverKind = leftoverKind
      }
      arr.push(item)
    }

    const arr: SpentBudgetItem[] = []
    pushSpentWithLeft(arr, t('adgroup.spentBudget.daySpent'), measures?.daySpent, measures?.dayBudget, 'day')

    if (adGroup.dailyBudgetType === DailyBudgetType.Auto) {
      arr.push({
        label: t('adgroup.details.dailyBudgetWithCurrency'),
        value: measures?.dayBudget,
      })
    }

    pushSpentWithLeft(arr, t('adgroup.spentBudget.monthSpent'), measures?.monthSpent, measures?.monthBudget, 'month')

    const cappingType = adGroup.budgetCappingType ?? (budget as { budgetCappingType?: string })?.budgetCappingType

    if (cappingType === 'Period') {
      pushSpentWithLeft(
        arr,
        t('adgroup.spentBudget.periodSpent'),
        measures?.periodSpent,
        measures?.periodBudget,
        'period'
      )
    }

    pushSpentWithLeft(
      arr,
      t('adgroup.spentBudget.overallSpent'),
      measures?.overallSpent,
      measures?.overallBudget,
      'overall'
    )

    return arr
  }, [budget, adGroup.dailyBudgetType, adGroup.budgetCappingType, measures])

  const renderSpentBudgets = spentBudgetsAndLabels.map((item, index) => {
    const isStandalone = item.value !== undefined
    const spentText =
      item.spent !== null && item.spent !== undefined && item.spent !== 0
        ? displayAsCurrency(item.spent)
        : t('commons.noValueYet')
    const leftSuffix =
      item.left !== undefined && item.leftoverKind !== undefined
        ? t('adgroup.spentBudget.leftAmount', { left: displayAsCurrency(item.left) })
        : null
    const leftColor =
      item.left !== undefined && item.leftoverKind !== undefined
        ? getLeftoverBudgetValueColor(item.leftoverKind, item.left, adGroup)
        : undefined

    return (
      <Box key={index} sx={{ flex: '1 1 0', minWidth: 0 }}>
        <StyledItemWrapper>
          <StyledLabel>{item.label}</StyledLabel>
          <StyledValueWrapper>
            <StyledValue sx={{ color: 'text.disabled' }} data-testid={item.label}>
              {isStandalone ? (
                item.value ? (
                  displayAsCurrency(item.value)
                ) : (
                  t('commons.noValueYet')
                )
              ) : (
                <>
                  {spentText}
                  {leftSuffix ? (
                    <Box component="span" sx={{ color: leftColor, ml: '0.35em' }}>
                      {leftSuffix}
                    </Box>
                  ) : null}
                </>
              )}
            </StyledValue>
          </StyledValueWrapper>
        </StyledItemWrapper>
      </Box>
    )
  })

  return (
    <FieldGroup size={8} spacing={0}>
      <Grid size={12}>
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', width: '100%', columnGap: 2 }}>
          {renderSpentBudgets}
          <Box sx={{ flex: '0 0 auto', alignSelf: 'center' }}>
            <BudgetHistoryButtons adGroupId={adGroup.id} />
          </Box>
        </Box>
      </Grid>
    </FieldGroup>
  )
}
export default SpentBudget
